import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { AccountService, ProjectService } from "../../services";
import { getFilteredOptions, selectAllOption } from "../../utils";
import { getCompanyCashAccountName, type NamedAccount } from "../../utils/accountUtils";
import { accountReportValidation } from "../../pages/report/validation";
import { ACCOUNT_REPORT_TYPE_OPTIONS } from "../../pages/report/constants";
import type { AccountReportQuery, CompanyAccountType, ReportType } from "../../services/analytics.types";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

/** A picker option; "all" is the synthetic select-all entry. */
interface PickerOption {
  id: number | string;
  [key: string]: unknown;
}

interface AccountReportFilters {
  accountIds: number[] | null;
  accountTypes: CompanyAccountType[] | null;
  projectIds: number[] | null;
  fromDate: string;
  toDate: string;
}

type FormErrors = Partial<Record<keyof AccountReportFilters, string>>;

interface AccountReportFormProps {
  reportType?: ReportType;
  handleClear?: () => void;
  onGenerate?: (payload: AccountReportQuery, options: { onError: () => void }) => void;
}

const ALL_OPTION = selectAllOption as PickerOption;

const isAccountType = (id: PickerOption["id"]): id is CompanyAccountType =>
  ACCOUNT_REPORT_TYPE_OPTIONS.some((option) => option.id === id);

const getAccountTypeOptions = () => Promise.resolve({ data: ACCOUNT_REPORT_TYPE_OPTIONS });

/**
 * Account picker filters: company account types only (no per-user USER accounts), with the owner
 * loaded so CASH accounts can be named. A module constant keeps the SelectField cache key stable.
 */
const ACCOUNT_PICKER_FILTERS = {
  type: ACCOUNT_REPORT_TYPE_OPTIONS.map((option) => option.id).join(","),
  includeOwnerDetails: true,
  sortBy: "name",
  sortOrder: "asc",
};

/** Minimal slice of the (still untyped) Redux store this form reads. */
interface CompanyOwnerState {
  user: { user?: { company?: { ownerUserId?: number | null } | null } | null };
}

export const AccountReportForm = ({
  reportType = "summary",
  handleClear = () => {},
  onGenerate = () => {},
}: AccountReportFormProps) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedAccounts, setSelectedAccounts] = useState<PickerOption[]>([ALL_OPTION]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState<PickerOption[]>([ALL_OPTION]);
  const [selectedProjects, setSelectedProjects] = useState<PickerOption[]>([ALL_OPTION]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const companyOwnerUserId = useSelector((state: CompanyOwnerState) => state.user.user?.company?.ownerUserId ?? null);

  // Name CASH accounts the same way the report rows and PDF do: "CASH (Company)" / "CASH (<owner>)"
  const getCompanyAccounts = useCallback(
    async (filters: Record<string, string | number | boolean>) => {
      const response = await AccountService.getAccounts(filters);
      const rows: NamedAccount[] = response?.data?.rows ?? [];

      return {
        ...response,
        data: {
          ...response?.data,
          rows: rows.map((account) => ({ ...account, name: getCompanyCashAccountName(account, companyOwnerUserId) })),
        },
      };
    },
    [companyOwnerUserId],
  );
  const [reportDetails, setReportDetails] = useState<AccountReportFilters>({
    accountIds: null,
    accountTypes: null,
    projectIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate" || name === "toDate") setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /** "All" clears the filter (every company account / type / project), otherwise the chosen ids are sent. */
  const handleMultiSelectChange =
    <K extends "accountIds" | "accountTypes" | "projectIds">(
      field: K,
      setSelected: React.Dispatch<React.SetStateAction<PickerOption[]>>,
      toValues: (options: PickerOption[]) => AccountReportFilters[K],
    ) =>
    (options: PickerOption[]) => {
      setLoading(false);
      handleClear();

      if (options.some((option) => option.id === "all")) {
        setSelected([ALL_OPTION]);
        setReportDetails((prev) => ({ ...prev, [field]: null }));
      } else {
        setSelected(options);
        setReportDetails((prev) => ({ ...prev, [field]: toValues(options) }));
      }

      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const toIds = (options: PickerOption[]): number[] =>
    options.map((option) => option.id).filter((id): id is number => typeof id === "number");
  const toAccountTypes = (options: PickerOption[]): CompanyAccountType[] =>
    options.map((option) => option.id).filter(isAccountType);

  const handleGenerateReport = async () => {
    setErrors({});
    const validationErrors: FormErrors = accountReportValidation(
      reportDetails,
      selectedAccounts[0]?.id,
      selectedAccountTypes[0]?.id,
      selectedProjects[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);

      const payload: AccountReportQuery = {
        ...reportDetails,
        fromDate: moment(reportDetails.fromDate).startOf("day").toISOString(),
        toDate: moment(reportDetails.toDate).endOf("day").toISOString(),
        reportType,
      };

      onGenerate(payload, { onError: () => setLoading(false) });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <CardLayout>
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <SelectField
            label="Account"
            value={selectedAccounts}
            getData={getCompanyAccounts}
            getDataStaticFilters={ACCOUNT_PICKER_FILTERS}
            required
            multiSelect
            error={!!errors.accountIds}
            helperText={errors.accountIds}
            onChange={handleMultiSelectChange("accountIds", setSelectedAccounts, toIds)}
            optionFilter={(options) => getFilteredOptions(options, selectedAccounts, selectAllOption)}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Account Type"
            value={selectedAccountTypes}
            getData={getAccountTypeOptions}
            required
            multiSelect
            error={!!errors.accountTypes}
            helperText={errors.accountTypes}
            onChange={handleMultiSelectChange("accountTypes", setSelectedAccountTypes, toAccountTypes)}
            optionFilter={(options) => getFilteredOptions(options, selectedAccountTypes, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Project"
            value={selectedProjects}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.projectIds}
            helperText={errors.projectIds}
            onChange={handleMultiSelectChange("projectIds", setSelectedProjects, toIds)}
            optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <ReportDateRangeFields
          fromDate={reportDetails.fromDate}
          toDate={reportDetails.toDate}
          onChange={updateDetailsState}
          errors={errors}
        />
        {largeRangeDialog}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
      </Box>
    </CardLayout>
  );
};
